import { isEqual } from 'lodash';

import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';
import { getHotelAmenitiesConfig } from '@apps/main/new-desktop/core/models';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { RecipeIngredient } from '@apps/main/modules/recipe/ingredients';
import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type {
  AccommodationProductCardContent,
  AlternativeHotel,
  AlternativeRoomSuggestionAccommodationSegment,
  AvailableItineraryAccommodationSegment,
  ItinerarySegment,
  ProductInfo,
  UnavailableItineraryAccommodationSegment,
} from '@apps/main/new-desktop/pages/tripOverview/models';
import {
  ItineraryAccommodationSegmentStatus,
  ItinerarySegmentType,
  ProductCardTopBarType,
  ProductCardType,
  UnavailabilityReason,
} from '@apps/main/new-desktop/pages/tripOverview/models';
import {
  getActiveFilters,
  getHotelFilterFromIngredient,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { getGuestRating } from '@apps/main/new-desktop/modules/customersRating';

import {
  isAvailableHotelIngredientResponse,
  isHotelIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import {
  AccommodationType,
  AvailabilityOrder,
  SearchTermType,
} from '@libs/trip-planner-interfaces';
import type {
  AvailableHotelIngredientResponseWithStaticInfo,
  ItineraryMainIngredientResponseWithStaticInfo,
  OverviewDestinationStaticInfo,
  UnavailableHotelIngredientResponseWithStaticInfo,
  HotelBookableOptionWithAlternativeRoom,
  HotelAlternativeOptionWithBasicRooms,
  IngredientId,
  ActivitySuggestionWithStaticInfo,
  ActivityStaticInfo,
  HotelBookableOptionWithBasicRooms,
  HotelBookableOptionWithPax,
  ChronologicalItinerary,
} from '@libs/trip-planner-interfaces';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import type { RouteConnectionWithDetails, RouteWithDetails } from '@libs/maps';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import { getChangeButtonText } from '../tripOverviewStoreLogic';

import {
  getActivitySegments,
  getUppsellSegment,
} from './tripOverviewActivitySegmentsLogic';
import {
  getTripOverviewDateLocationSegment,
  getTripOverviewMiddleLocationSegment,
} from './tripOverviewLocationSegmentsLogic';

const { t } = i18n.global;

export const REQUSTED_IMAGE_SIZE = 800;
const TOP_AMENITIES_NUMBER = 6;

export function getHotelRelatedSegments(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  isSuggestedHotel: (ingredientId: IngredientId, hotelCode: string) => boolean,
  areThereActivitiesInDestination: boolean,
  activitySuggestions: ActivitySuggestionWithStaticInfo[],
  staticActivitySuggestions: ActivityStaticInfo[],
  ingredientIndex: number,
  moneyFormanterFn: (amount: number, currency: string) => string,
  itineraryByPeriod: ChronologicalItinerary,
  isDateLocationSegmentExperiment: boolean,
  selectedDate?: Date
): ItinerarySegment[] {
  const segments: ItinerarySegment[] = [];

  if (!isHotelIngredientResponseWithStaticInfo(ingredient)) {
    return segments;
  }

  const hotelIngredient = getIngredient(
    ingredient.ingredientId
  ) as HotelIngredient;

  if (isDateLocationSegmentExperiment) {
    segments.push(
      getTripOverviewDateLocationSegment(itineraryByPeriod, {
        ingredientId: ingredient.ingredientId,
        numberOfNights: ingredient.numberOfNights,
        displayText: hotelIngredient.displayText,
        mainPolygonFullName: ingredient.staticInfo?.mainPolygon?.fullName,
      })
    );
  } else {
    segments.push(
      getTripOverviewMiddleLocationSegment({
        ingredientId: ingredient.ingredientId,
        numberOfNights: ingredient.numberOfNights,
        displayText: hotelIngredient.displayText,
        mainPolygonFullName: ingredient.staticInfo?.mainPolygon?.fullName,
      })
    );
  }

  if (
    isAvailableHotelIngredientResponse(ingredient) &&
    !isFeatureToggleActive('show-availability-errors')
  ) {
    handleAvailableHotel(
      ingredient,
      hotelIngredient,
      ingredientIndex,
      segments,
      isSuggestedHotel
    );
  } else {
    handleUnavailableHotel(
      ingredient,
      hotelIngredient,
      ingredientIndex,
      segments,
      selectedDate
    );
  }

  segments.push(
    ...getActivitySegments(
      ingredient,
      getIngredient,
      ingredientIndex,
      selectedDate
    )
  );

  const upsellSegment = getUppsellSegment(
    hotelIngredient,
    areThereActivitiesInDestination,
    activitySuggestions,
    staticActivitySuggestions,
    moneyFormanterFn
  );

  if (upsellSegment) {
    segments.push(upsellSegment);
  }

  return segments;
}

function handleAvailableHotel(
  ingredient: AvailableHotelIngredientResponseWithStaticInfo,
  hotelIngredient: HotelIngredient,
  ingredientIndex: number,
  segments: ItinerarySegment[],
  isSuggestedHotel: (ingredientId: IngredientId, hotelCode: string) => boolean
): void {
  const isAccommodationSuggested = isSuggestedHotel(
    ingredient.ingredientId,
    ingredient.staticInfo.hotelCode
  );

  segments.push(
    getTripOverviewHotelSegment(
      ingredient,
      isAccommodationSuggested,
      hotelIngredient,
      ingredientIndex
    )
  );
}

function handleUnavailableHotel(
  ingredient:
    | AvailableHotelIngredientResponseWithStaticInfo
    | UnavailableHotelIngredientResponseWithStaticInfo,
  hotelIngredient: HotelIngredient,
  ingredientIndex: number,
  segments: ItinerarySegment[],
  selectedDate?: Date
): void {
  if (
    ingredient.alternativeSuggestion &&
    ingredient.staticInfo &&
    !isFeatureToggleActive('show-availability-errors')
  ) {
    segments.push(
      getTripOverviewAlternativeRoomSegment(
        hotelIngredient,
        ingredient,
        ingredient.alternativeSuggestion,
        ingredientIndex
      )
    );
  } else {
    segments.push(
      getTripOverviewUnavailableHotelSegment(
        hotelIngredient,
        ingredient,
        ingredientIndex,
        selectedDate
      )
    );
  }
}

export function getRoute(
  destination: OverviewDestinationStaticInfo | string,
  calculatedRoute: RouteWithDetails
): RouteConnectionWithDetails | undefined {
  return calculatedRoute.find((route: RouteConnectionWithDetails) => {
    const markerData = route.to.data;
    if (typeof destination === 'string') {
      return route.to.key.includes(destination);
    } else {
      return (
        markerData?.hotelCode === destination.hotelCode &&
        markerData.ingredientId === destination.ingredientId
      );
    }
  });
}

export function getTripOverviewHotelSegment(
  ingredient: AvailableHotelIngredientResponseWithStaticInfo,
  isSuggested: boolean,
  hotelIngredient: HotelIngredient,
  ingredientIndex: number
): AvailableItineraryAccommodationSegment {
  const statusType = ItineraryAccommodationSegmentStatus.Available;

  let productCardTopBarType: ProductCardTopBarType;

  if (isSuggested) {
    productCardTopBarType =
      hotelIngredient.availabilityOrder === AvailabilityOrder.Cheapest
        ? ProductCardTopBarType.Cheapest
        : ProductCardTopBarType.Suggestion;
  } else {
    productCardTopBarType = ProductCardTopBarType.Selected;
  }

  const filters = hotelIngredient.filters
    ? getActiveFilters(getHotelFilterFromIngredient(hotelIngredient.filters))
    : [];

  return {
    segmentType: ItinerarySegmentType.Accommodation,
    statusType,
    productCardTopBarType,
    productCardData: {
      imgSrcs: ingredient.staticInfo.images.map((image) =>
        getImageUrl(image, undefined, REQUSTED_IMAGE_SIZE, REQUSTED_IMAGE_SIZE)
      ),
      productCardContent: getAccommodationProductCardContent(ingredient),
      date: getAccommodationDate(ingredient.date, ingredient.numberOfNights),
    },
    context: {
      ingredientId: ingredient.ingredientId,
      hotelCode: ingredient.staticInfo.hotelCode,
      accommodationType: ingredient.staticInfo.typeOfHotel,
      ingredientIndex,
    },
    addToBasketToken: ingredient.addToBasketToken,
    filters,
    alternativeHotels: getAlternativeHotels(
      ingredient.suggestions,
      ingredient.suggestionsStaticInfo,
      ingredient.suggested.code
    ),
  };
}

function getAccommodationDate(date: Date, numberOfNights: number): string {
  let displayDate = `${new SemboDate(date).format(DateFormat.DateMonth)}`;

  if (numberOfNights) {
    displayDate += ` - ${new SemboDate(date)
      .add({ days: numberOfNights })
      .format(DateFormat.DateMonth)}`;
  }

  return displayDate;
}

function getTopHotelAmenities(amenities, amount: number): HotelAmenityData[][] {
  if (Object.keys(amenities).length === 0) {
    return [];
  }

  const config = getHotelAmenitiesConfig();
  const topAmenities = Object.keys(amenities)
    .filter((key) => Boolean(amenities[key]))
    .slice(0, amount)
    .map((key) => config[key])
    .sort((a, b) => b.value.length - a.value.length);

  return [
    topAmenities.slice(topAmenities.length / 2),
    topAmenities.slice(0, topAmenities.length / 2),
  ];
}

function getAlternativeHotels(
  suggestions: HotelAlternativeOptionWithBasicRooms[],
  sugesstionsStaticInfo: OverviewDestinationStaticInfo[],
  currentHotelCode: string
): AlternativeHotel[] {
  return suggestions.reduce(
    (
      acc: AlternativeHotel[],
      suggestion: HotelAlternativeOptionWithBasicRooms
    ) => {
      if (suggestion.code === currentHotelCode) {
        return acc;
      }
      const staticInfo = sugesstionsStaticInfo.find(
        (info) => info.hotelCode === suggestion.code
      );

      if (staticInfo) {
        // At Marcus's request, the mealName pills have been temporarily hidden to prevent confusion among customers.
        const mealName = getMealName(suggestion);
        const guestRating = getGuestRating(staticInfo.guestRatings);
        const imgUrls = staticInfo.images.map((image) => getImageUrl(image));
        acc.push({
          imgUrls,
          hotelName: staticInfo.name,
          polygonName: staticInfo.mainPolygon?.fullName || '',
          deltaPrice: suggestion.prices.deltaPrice,
          hotelCode: suggestion.code,
          accommodationType: staticInfo.typeOfHotel,
          ingredientId: staticInfo.ingredientId,
          stars: staticInfo.semboSunRating,
          amenities: getTopHotelAmenities(
            staticInfo.amenities,
            TOP_AMENITIES_NUMBER
          ),
          guestRating,
          roomsInfo:
            suggestion.rooms.map((room, index) => ({
              label: `${t('planner.pax.rooms')} ${index + 1}`,
              description: room.description,
            })) || [],
          isRefundable: suggestion.isRefundable,
          mealName,
        });
      }

      return acc;
    },
    []
  );
}

export function getTripOverviewAlternativeRoomSegment(
  hotelIngredient: HotelIngredient,
  ingredient:
    | AvailableHotelIngredientResponseWithStaticInfo
    | UnavailableHotelIngredientResponseWithStaticInfo,
  alternativeSuggestion: HotelBookableOptionWithAlternativeRoom,
  ingredientIndex: number
): AlternativeRoomSuggestionAccommodationSegment {
  const filters = hotelIngredient.filters
    ? getActiveFilters(getHotelFilterFromIngredient(hotelIngredient.filters))
    : [];
  return {
    segmentType: ItinerarySegmentType.Accommodation,
    statusType: ItineraryAccommodationSegmentStatus.AlternativeRoom,
    productCardTopBarType: ProductCardTopBarType.Suggestion,
    productCardData: {
      imgSrcs:
        ingredient.staticInfo?.images.map((image) =>
          getImageUrl(
            image,
            undefined,
            REQUSTED_IMAGE_SIZE,
            REQUSTED_IMAGE_SIZE
          )
        ) || [],
      productCardContent: getAccommodationProductCardContent(ingredient, true),
      date: getAccommodationDate(ingredient.date, ingredient.numberOfNights),
    },
    context: {
      ingredientId: ingredient.ingredientId,
      hotelCode: ingredient.staticInfo?.hotelCode || '',
      accommodationType:
        ingredient.staticInfo?.typeOfHotel || AccommodationType.Hotel,
      ingredientIndex,
    },
    alternativeSuggestionData: {
      hotelCode: alternativeSuggestion.code,
      isRefundable: alternativeSuggestion.isRefundable,
      rooms: alternativeSuggestion.rooms.map((room) => ({
        roomCode: room.roomCode,
        mealCode: room.boardBasis,
      })),
    },
    filters,
  };
}

export function getTripOverviewUnavailableHotelSegment(
  hotelIngredient: HotelIngredient,
  ingredient:
    | AvailableHotelIngredientResponseWithStaticInfo
    | UnavailableHotelIngredientResponseWithStaticInfo,
  ingredientIndex: number,
  selectedDate?: Date
): UnavailableItineraryAccommodationSegment {
  const filters = hotelIngredient.filters
    ? getActiveFilters(getHotelFilterFromIngredient(hotelIngredient.filters))
    : [];
  return {
    segmentType: ItinerarySegmentType.Accommodation,
    productCardData: null,
    statusType: ItineraryAccommodationSegmentStatus.NoAvailable,
    context: {
      ingredientId: ingredient.ingredientId,
      ingredientIndex,
    },
    reasons: getUnavailableHotelSegmentReasons(hotelIngredient, selectedDate),
    filters,
  };
}

export function getUnavailableHotelSegmentReasons(
  ingredient: HotelIngredient,
  selectedDate?: Date
): UnavailabilityReason[] {
  const reasons: UnavailabilityReason[] = [];

  const hasSelectedProduct = ingredient.heartedHotels.length > 0;
  const hasSelectedFilters = !isEqual(
    ingredient.filters,
    HotelIngredient.defaultFilters
  );

  if (selectedDate) {
    reasons.push(UnavailabilityReason.SelectedDate);
  }

  if (hasSelectedProduct) {
    reasons.push(UnavailabilityReason.SelectedProduct);
  } else if (hasSelectedFilters) {
    reasons.push(UnavailabilityReason.SelectedFilters);
  } else if (ingredient.searchTerm.type === SearchTermType.HotelCode) {
    reasons.push(UnavailabilityReason.SuggestedFromHotelCodeSearch);
  } else {
    reasons.push(UnavailabilityReason.Suggested);
  }

  return reasons;
}

export function getUnavailableAccommodationMessage(
  reasons: UnavailabilityReason[]
): string {
  if (isEqual(reasons, [UnavailabilityReason.SelectedDate])) {
    return t('planner.no_availability.messages.hotel_date_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedProduct])) {
    return t('planner.no_availability.messages.hotel_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedFilters])) {
    return t('planner.no_availability.messages.hotel_with_filters');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedProduct)
  ) {
    return t('planner.no_availability.messages.hotel_selected_date_selected');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedFilters)
  ) {
    return t(
      'planner.no_availability.messages.hotel_date_selected_with_filters'
    );
  }

  return t('notification.no_available_with_current_filters');
}

export function getAccommodationProductCardContent(
  ingredient:
    | AvailableHotelIngredientResponseWithStaticInfo
    | UnavailableHotelIngredientResponseWithStaticInfo,
  shouldUseAlternativeSuggestion = false
): AccommodationProductCardContent {
  if (!ingredient.staticInfo) {
    throw new Error('No static info in hotel ingredient');
  }

  const location =
    ingredient.staticInfo.mainPolygon?.fullName ||
    `${ingredient.staticInfo.address.city}${
      ingredient.staticInfo.address.country
        ? `, ${ingredient.staticInfo.address.country}`
        : ''
    }`;
  // NOTE: Markus requested to hide cancellation policy label from overview card since it’s more related to room than hotel info. Just commented for now until final design
  // const cancellationPolicies =
  //   ingredient.suggested && preferredDay.value
  //     ? getHotelStayTag(
  //         ingredient.suggested?.isRefundable,
  //         ingredient.suggested?.rooms,
  //         preferredDay.value
  //       )
  //     : undefined;

  const mealName = getMealName(ingredient.suggested);

  const guestRating = getGuestRating(ingredient.staticInfo.guestRatings);

  return {
    header: ingredient.staticInfo.name,
    subheader: location,
    rating: {
      stars: ingredient.staticInfo.semboSunRating,
    },
    cardDetails: getAccommodationProductCardDetails(
      ingredient,
      shouldUseAlternativeSuggestion
    ),
    changeButtonText: getChangeButtonText(ProductCardType.Accommodation),
    amenities: getTopHotelAmenities(
      ingredient.staticInfo.amenities,
      TOP_AMENITIES_NUMBER
    ),
    cancellationPolicies: undefined,
    mealName,
    guestRating,
  };
}

export function getAccommodationProductCardDetails(
  ingredient:
    | AvailableHotelIngredientResponseWithStaticInfo
    | UnavailableHotelIngredientResponseWithStaticInfo,
  shouldUseAlternativeSuggestions: boolean
): ProductInfo[] {
  const rooms = shouldUseAlternativeSuggestions
    ? ingredient.alternativeSuggestion?.rooms
    : ingredient.suggested?.rooms;
  return (
    rooms?.map((room, index) => ({
      label: `${t('planner.pax.rooms')} ${index + 1}`,
      description: room.description,
    })) || []
  );
}

function getMealName(
  bookableOption:
    | HotelBookableOptionWithBasicRooms
    | HotelBookableOptionWithPax
    | null
): string | undefined {
  // At Marcus's request, the mealName pills have been temporarily hidden to prevent confusion among customers.
  // https://semboteam.slack.com/archives/C07HTF5381K/p1738147275002449
  return bookableOption?.rooms[0]?.boardBasis
    ? undefined // ? getBoardBasisText(bookableOption.rooms[0].boardBasis)
    : undefined;
}
